import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

/**
 * Rodapé com razão social + CNPJ + dados de contato — exigência
 * LGPD Art. 9º (transparência do controlador) + Art. 41 (DPO).
 *
 * Busca de /api/legal/platform (público, sem auth). Cai de forma
 * graciosa quando o singleton ainda não foi preenchido: oculta os
 * campos vazios em vez de mostrar "null".
 *
 * Variantes:
 *   - dark  (default): pra Landing.tsx e Pricing.tsx (fundo escuro/cinza)
 *   - light:          pra páginas legais/help (fundo claro)
 *   - compact:        só uma linha (pra views minimalistas)
 */

interface PlatformInfo {
  dpoName: string | null
  dpoEmail: string | null
  privacyEmail: string | null
  companyLegalName: string | null
  companyCnpj: string | null
}

function fmtCnpj(raw: string): string {
  // 12.345.678/0001-90
  const digits = raw.replace(/\D/g, '').padStart(14, '0').slice(-14)
  if (digits.length !== 14) return raw
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`
}

export function CompanyFooter({ variant = 'dark' }: { variant?: 'dark' | 'light' | 'compact' }) {
  const [info, setInfo] = useState<PlatformInfo | null>(null)

  useEffect(() => {
    fetch('/api/legal/platform')
      .then(r => r.ok ? r.json() : null)
      .then(setInfo)
      .catch(() => setInfo(null))
  }, [])

  const year = new Date().getFullYear()
  const hasCompany = !!(info?.companyLegalName)
  const hasCnpj = !!(info?.companyCnpj)
  const hasPrivacyEmail = !!(info?.privacyEmail)

  const company = info?.companyLegalName ?? 'Pakt Technologies'
  const cnpjLine = hasCnpj ? ` · CNPJ ${fmtCnpj(info!.companyCnpj!)}` : ''
  const privacyLine = hasPrivacyEmail
    ? ` · Encarregado de dados: ${info!.privacyEmail}`
    : ''

  if (variant === 'compact') {
    return (
      <div className="text-xs text-gray-500 text-center py-3 font-mono">
        © {year} {company}{cnpjLine}
      </div>
    )
  }

  const isDark = variant === 'dark'
  const wrapBg = isDark
    ? 'bg-pakt-ink/95 text-white/40 border-t border-white/5'
    : 'bg-gray-50 text-gray-500 border-t border-gray-200'
  const strong = isDark ? 'text-white/70' : 'text-gray-700'
  const linkClass = isDark ? 'hover:text-white' : 'hover:text-gray-900'

  return (
    <footer className={`${wrapBg} py-8`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm justify-center items-center mb-4">
          <Link to="/termos" className={linkClass}>Termos</Link>
          <Link to="/privacy" className={linkClass}>Privacidade</Link>
          <Link to="/sub-processadores" className={linkClass}>Sub-processadores</Link>
          <Link to="/dpa" className={linkClass}>DPA</Link>
          <Link to="/privacidade/exercer-direitos" className={linkClass}>Exercer direitos (LGPD)</Link>
          <Link to="/verify" className={linkClass}>Verificar documento</Link>
        </div>

        <div className={`text-xs leading-relaxed text-center font-mono ${variant === 'dark' ? 'text-white/30' : 'text-gray-400'}`}>
          <div>
            © {year} <span className={strong}>{company}</span>
            {cnpjLine}
          </div>
          {(hasPrivacyEmail || info?.dpoName) && (
            <div className="mt-1">
              {info?.dpoName && (
                <>DPO: <span className={strong}>{info.dpoName}</span></>
              )}
              {info?.dpoName && hasPrivacyEmail && <span> · </span>}
              {hasPrivacyEmail && (
                <>Canal de privacidade: <a href={`mailto:${info!.privacyEmail}`} className={linkClass}>{info!.privacyEmail}</a></>
              )}
            </div>
          )}
          {!hasCompany && (
            // Hint dev/staging: avisa visualmente quando platform_settings ainda
            // não foi preenchido (só aparece em DEV; em prod o admin já configurou).
            (typeof window !== 'undefined' && window.location.hostname === 'localhost') && (
              <div className="mt-2 text-pakt-warning">
                ⚠ Preencha razão social + CNPJ em /admin/platform-settings
              </div>
            )
          )}
        </div>
      </div>
    </footer>
  )
}
